import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  MdBusiness, MdWarning, MdCheckCircle, MdTrendingUp,
  MdErrorOutline, MdOpenInNew, MdPeople, MdRefresh, MdExpandMore,
  MdStorage, MdCloud, MdEmail, MdLink,
} from 'react-icons/md';
import { FaUserSecret, FaWhatsapp } from 'react-icons/fa';
import toast from 'react-hot-toast';
import saApi from '../../services/superAdminApi';
import { humanizeAction } from './auditActions';

// ── Action accent colors (dashboard feed dots) ────────────────────────────────
const ACTION_ACCENT = {
  SA_SUSPEND_CLUB:        '#f87171',
  SA_REACTIVATE_CLUB:     '#34d399',
  SA_START_IMPERSONATION: '#f97316',
  SA_UPDATE_SUBSCRIPTION: '#fbbf24',
  SA_UPDATE_TEAM_ROLE:    '#a78bfa',
  SA_ADD_CLUB_NOTE:       '#60a5fa',
};

// Low-value actions filtered out of the Platform Actions feed (logins, dashboard views).
const NOISE_ACTIONS = new Set(['SA_LOGIN', 'SA_VIEW_DASHBOARD']);

const RISK_COLORS = {
  high:   { bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.3)',   text: '#f87171', label: 'HIGH'   },
  medium: { bg: 'rgba(251,191,36,0.10)',  border: 'rgba(251,191,36,0.3)',  text: '#fbbf24', label: 'MED'    },
  low:    { bg: 'rgba(107,114,128,0.12)', border: 'rgba(107,114,128,0.3)', text: '#8a847c', label: 'LOW'    },
};

const fmtNPR = (n) => (n == null ? '—' : `NPR ${Number(n).toLocaleString()}`);

function fmtTimeAgo(ts) {
  const m = Math.floor((Date.now() - new Date(ts).getTime()) / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ── Small presentational helpers ──────────────────────────────────────────────
function SectionLabel({ children, action }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-3">
      <h2 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6b6560' }}>{children}</h2>
      {action}
    </div>
  );
}

function KeyCard({ label, value, sub, accent, icon: Icon }) {
  return (
    <div className="rounded-xl border p-4" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-2xl font-extrabold text-[#ede8e0] truncate">{value ?? '—'}</div>
          <div className="text-xs font-semibold mt-0.5" style={{ color: accent || '#8a847c' }}>{label}</div>
          {sub && <div className="text-xs mt-0.5" style={{ color: '#6b6560' }}>{sub}</div>}
        </div>
        {Icon && (
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `${accent || '#8a847c'}18` }}>
            <Icon style={{ color: accent || '#8a847c', fontSize: 16 }} />
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs" style={{ color: '#6b6560' }}>{label}</span>
      <span className="text-sm font-semibold text-[#ede8e0]">{value}</span>
    </div>
  );
}

function AlertBanner({ type, message, linkTo, linkLabel }) {
  const isWarn = type === 'warn';
  const col = isWarn ? { bg: 'rgba(251,191,36,0.08)', border: 'rgba(251,191,36,0.25)', text: '#fbbf24' }
                     : { bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.25)', text: '#f87171' };
  return (
    <div className="flex items-center justify-between rounded-xl px-4 py-3"
      style={{ background: col.bg, border: `1px solid ${col.border}` }}>
      <div className="flex items-center gap-2">
        <MdWarning style={{ color: col.text, flexShrink: 0 }} />
        <span className="text-sm" style={{ color: col.text }}>{message}</span>
      </div>
      {linkTo && (
        <Link to={linkTo} className="text-xs font-semibold hover:underline flex-shrink-0 ml-3"
          style={{ color: col.text }}>
          {linkLabel || 'View →'}
        </Link>
      )}
    </div>
  );
}

// ── Production Health — collapsed to one line, expandable ──────────────────────
const HEALTH_CHECKS = [
  { key: 'server',     label: 'Backend Server',  Icon: MdBusiness  },
  { key: 'database',   label: 'Database',         Icon: MdStorage   },
  { key: 'cloudinary', label: 'Cloudinary',       Icon: MdCloud     },
  { key: 'email',      label: 'Email (Resend)',   Icon: MdEmail     },
  { key: 'frontendUrl',label: 'Frontend URL',     Icon: MdLink      },
];

function HealthPanel() {
  const [open, setOpen] = useState(false);
  const { data, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey:  ['production-health'],
    queryFn:   () => saApi.get('/api/health/details').then(r => r.data),
    staleTime: 30000,
    retry:     1,
  });

  const checks   = data?.checks || {};
  const allOk    = data?.status === 'ok';
  const lastSeen = dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;
  const dotColor = data ? (allOk ? '#34d399' : '#f87171') : '#6b6560';
  const summary  = !data ? 'Checking systems…' : allOk ? 'All systems operational' : 'Some systems degraded';

  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'rgba(237,232,224,0.08)', background: '#1e1d1b' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor }} />
          <span className="text-sm text-[#ede8e0] truncate">{summary}</span>
          {lastSeen && <span className="text-xs flex-shrink-0" style={{ color: '#6b6560' }}>· checked {lastSeen}</span>}
        </div>
        <MdExpandMore style={{ color: '#6b6560', fontSize: 18, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && (
        <>
          <div className="divide-y" style={{ '--tw-divide-opacity': 1, borderColor: 'rgba(237,232,224,0.08)' }}>
            {HEALTH_CHECKS.map(({ key, label, Icon }) => {
              const ok      = checks[key];
              const loading = !data && isFetching;
              return (
                <div key={key} className="flex items-center justify-between px-4 py-2.5 border-t" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
                  <div className="flex items-center gap-2.5">
                    <Icon style={{ color: '#6b6560', fontSize: 15, flexShrink: 0 }} />
                    <span className="text-sm" style={{ color: '#c5bfb5' }}>{label}</span>
                  </div>
                  {loading ? (
                    <div className="w-16 h-5 rounded animate-pulse" style={{ background: '#28261f' }} />
                  ) : data ? (
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{
                      background: ok ? 'rgba(52,211,153,0.12)' : 'rgba(248,113,113,0.12)',
                      color:      ok ? '#34d399' : '#f87171',
                    }}>
                      {ok ? (key === 'database' ? 'Connected' : key === 'server' ? 'Healthy' : 'Configured') : 'Missing'}
                    </span>
                  ) : (
                    <span className="text-xs" style={{ color: '#6b6560' }}>—</span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="px-4 py-2 border-t flex items-center justify-between gap-3" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
            <div className="text-xs flex items-center gap-2 flex-wrap" style={{ color: '#6b6560' }}>
              {data && (
                <>
                  <span>env: {data.environment}</span>
                  <span>·</span>
                  <span>uptime: {Math.floor(data.uptime / 3600)}h {Math.floor((data.uptime % 3600) / 60)}m</span>
                  <span>·</span>
                  <span>v{data.version}</span>
                </>
              )}
            </div>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg transition-colors flex-shrink-0"
              style={{ background: '#28261f', color: isFetching ? '#6b6560' : '#8a847c' }}
            >
              <MdRefresh className={isFetching ? 'animate-spin' : ''} style={{ fontSize: 13 }} />
              Refresh
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Sales helpers ─────────────────────────────────────────────────────────────
function buildWaMsg(lead) {
  const clubLine = lead.clubName ? `I saw your request for ${lead.clubName}.\n` : '';
  return (
    `Namaste ${lead.name},\n` +
    `Thank you for your interest in TrainAxis.\n\n` +
    `${clubLine}` +
    `TrainAxis helps clubs manage students, attendance, monthly fees, QR payments, parent reminders, and reports.\n\n` +
    `Would you like a quick demo/setup call?\n\n` +
    `- TrainAxis Team`
  );
}

function fmtNepalPhone(raw) {
  if (!raw) return '';
  const d = raw.replace(/[^\d]/g, '');
  if (!d) return '';
  if (d.startsWith('977') && d.length >= 12) return d;
  if (d.startsWith('0') && d.length >= 10) return '977' + d.slice(1);
  if (d.length === 10 && (d.startsWith('98') || d.startsWith('97'))) return '977' + d;
  return d;
}

// ── Follow-up row (used inside Needs Attention) ───────────────────────────────
function FollowUpRow({ lead }) {
  const waNum = fmtNepalPhone(lead.phone);
  const waUrl = lead.phone ? `https://wa.me/${waNum}?text=${encodeURIComponent(buildWaMsg(lead))}` : null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
      <div className="min-w-0 mr-3">
        <p className="text-sm font-semibold text-[#ede8e0] truncate">{lead.name}</p>
        <p className="text-xs truncate" style={{ color: '#8a847c' }}>
          {lead.clubName || lead.phone || lead.email || '—'}
          {lead.lastContactedAt && <span style={{ color: '#6b6560' }}> · last {fmtTimeAgo(lead.lastContactedAt)}</span>}
        </p>
      </div>
      {waUrl ? (
        <a href={waUrl} target="_blank" rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: '#16a34a', color: '#fff', borderRadius: 6, padding: '4px 9px',
            fontSize: 11, fontWeight: 600, textDecoration: 'none', flexShrink: 0,
          }}>
          <FaWhatsapp style={{ fontSize: 12 }} /> WA
        </a>
      ) : (
        <span className="text-xs flex-shrink-0" style={{ color: '#6b6560' }}>No phone</span>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function SuperDashboard() {
  const { data, isPending } = useQuery({
    queryKey:       ['sa-stats'],
    queryFn:        () => saApi.get('/api/super-admin/dashboard/stats').then(r => r.data),
    refetchInterval: 60000,
  });

  // Same query key as the sales views — React Query dedupes, so no new API call.
  const { data: salesData } = useQuery({
    queryKey:  ['sa-sales-summary'],
    queryFn:   () => saApi.get('/api/super-admin/sales-summary').then(r => r.data),
    staleTime: 60000,
  });

  const stats       = data?.stats           || {};
  const rev         = stats.revenue         || {};
  const risk        = stats.riskSummary     || {};
  const dw          = stats.discountWarnings || {};
  const atRisk      = data?.atRiskClubs      || [];
  // Hide low-value noise (logins, dashboard views) so real events aren't buried, then cap at 6.
  const activity    = (data?.recentActivity || [])
    .filter(log => !NOISE_ACTIONS.has(log.action))
    .slice(0, 6);
  const impersonations = (data?.recentImpersonations || []).slice(0, 6);

  const followUpsDue = salesData?.followUpsDue || [];
  const mainDemo     = salesData?.mainDemo;
  const demoUrl      = mainDemo?.slug ? `${window.location.origin}/demo/${mainDemo.slug}` : null;
  const copyDemo = () => {
    if (!demoUrl) return;
    navigator.clipboard.writeText(demoUrl)
      .then(() => toast.success('Demo link copied'))
      .catch(() => toast.error('Copy failed'));
  };

  const nothingToDo = atRisk.length === 0 && followUpsDue.length === 0;

  const quickActions = [
    { href: '/superadmin/clubs?status=trial',     label: 'Expiring Trials', accent: '#fbbf24', count: stats.expiringTrials7d },
    { href: '/superadmin/clubs?status=suspended', label: 'Suspended',       accent: '#f87171', count: stats.suspendedClubs   },
    { href: '/superadmin/subscriptions',          label: 'Subscriptions',   accent: '#a78bfa' },
    { href: '/superadmin/discounts',              label: 'Discounts',       accent: '#f97316' },
    { href: '/superadmin/clubs',                  label: 'All Clubs',       accent: '#60a5fa' },
    { href: '/superadmin/audit-logs',             label: 'Audit Logs',      accent: '#8a847c' },
  ];

  return (
    <div className="space-y-8 max-w-7xl">

      {/* ── Header ── */}
      <div>
        <h1 className="text-2xl font-bold text-[#ede8e0]">Command Center</h1>
        <p className="text-sm mt-1" style={{ color: '#8a847c' }}>Platform health and revenue at a glance</p>
      </div>

      {/* 1 ── Alerts (only when present) ── */}
      {!isPending && (
        stats.pendingPlatformPayments > 0 || stats.expiringTrials > 0 || stats.expiringTrials7d > stats.expiringTrials ||
        stats.suspendedClubs > 0 || rev.expiringDiscounts7d > 0 || dw.stale > 0 || rev.revenueAtRisk > 0
      ) && (
        <div className="space-y-2">
          {stats.pendingPlatformPayments > 0 && (
            <AlertBanner type="warn" message={`${stats.pendingPlatformPayments} payment${stats.pendingPlatformPayments > 1 ? 's' : ''} awaiting verification`}
              linkTo="/superadmin/subscriptions#pending-payments" linkLabel="Review →" />
          )}
          {stats.expiringTrials > 0 && (
            <AlertBanner type="error" message={`${stats.expiringTrials} trial${stats.expiringTrials > 1 ? 's' : ''} expiring within 3 days`}
              linkTo="/superadmin/clubs?status=trial" linkLabel="View trials →" />
          )}
          {stats.expiringTrials7d > stats.expiringTrials && (
            <AlertBanner type="warn" message={`${stats.expiringTrials7d - stats.expiringTrials} more trial${stats.expiringTrials7d - stats.expiringTrials > 1 ? 's' : ''} expiring in 4–7 days`}
              linkTo="/superadmin/clubs?status=trial" linkLabel="View →" />
          )}
          {stats.suspendedClubs > 0 && (
            <AlertBanner type="error" message={`${stats.suspendedClubs} club${stats.suspendedClubs > 1 ? 's' : ''} currently suspended`}
              linkTo="/superadmin/clubs?status=suspended" linkLabel="View suspended →" />
          )}
          {rev.expiringDiscounts7d > 0 && (
            <AlertBanner type="warn" message={`${rev.expiringDiscounts7d} discount${rev.expiringDiscounts7d > 1 ? 's' : ''} expiring within 7 days`}
              linkTo="/superadmin/discounts" linkLabel="View discounts →" />
          )}
          {dw.stale > 0 && (
            <AlertBanner type="warn" message={`${dw.stale} expired discount${dw.stale > 1 ? 's' : ''} still marked active — MRR may be understated`}
              linkTo="/superadmin/discounts" linkLabel="Clean up →" />
          )}
          {rev.revenueAtRisk > 0 && (
            <AlertBanner type="warn" message={`${fmtNPR(rev.revenueAtRisk)}/mo at risk — suspended Pro or imminent discount loss`}
              linkTo="/superadmin/subscriptions" linkLabel="View →" />
          )}
        </div>
      )}

      {isPending ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-xl border p-4 animate-pulse" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
              <div className="h-7 w-24 rounded" style={{ background: '#28261f' }} />
              <div className="h-3 w-16 rounded mt-2" style={{ background: '#28261f' }} />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* 2 ── Key metrics ── */}
          <section>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KeyCard label="Net MRR"      value={fmtNPR(rev.netMrr)}          sub="Monthly recurring"                 accent="#34d399" icon={MdTrendingUp} />
              <KeyCard label="Active Clubs" value={stats.activeClubs ?? 0}      sub={`of ${stats.totalClubs ?? 0} total`} accent="#60a5fa" icon={MdBusiness} />
              <KeyCard label="At Risk"      value={risk.total ?? 0}             sub="clubs flagged"                     accent={(risk.total ?? 0) > 0 ? '#f87171' : '#6b6560'} icon={MdErrorOutline} />
              <KeyCard label="New Leads"    value={salesData ? (salesData.totalLeadsThisMonth ?? 0) : '—'} sub="Last 30 days" accent="#a78bfa" icon={MdPeople} />
            </div>

            {/* Quiet details row */}
            <div className="mt-3 rounded-xl border px-5 py-4" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
              <div className="flex flex-wrap gap-x-8 gap-y-3">
                <MiniStat label="Gross MRR" value={fmtNPR(rev.grossMrr)} />
                <MiniStat label="Discounts" value={rev.discountsGiven > 0 ? `−${fmtNPR(rev.discountsGiven)}` : 'NPR 0'} />
                <MiniStat label="ARPU"      value={fmtNPR(rev.averageRevenuePerProClub)} />
                <MiniStat label="Pro"       value={stats.proClubs ?? 0} />
                <MiniStat label="Free"      value={stats.freeClubs ?? 0} />
                <MiniStat label="Trial"     value={stats.trialClubs ?? 0} />
                <MiniStat label="Students"  value={(stats.totalUsers ?? 0).toLocaleString()} />
              </div>
            </div>
          </section>

          {/* 3 ── Needs attention (merged work queue) ── */}
          <section>
            <SectionLabel>Needs Attention</SectionLabel>
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'rgba(237,232,224,0.08)', background: '#1e1d1b' }}>
              {nothingToDo ? (
                <div className="px-5 py-6 flex items-center gap-2">
                  <MdCheckCircle style={{ color: '#34d399', fontSize: 18 }} />
                  <span className="text-sm" style={{ color: '#8a847c' }}>Nothing needs your attention</span>
                </div>
              ) : (
                <>
                  {atRisk.length > 0 && (
                    <div>
                      <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider"
                        style={{ color: '#8a847c', background: '#1a1918', borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
                        At-risk clubs
                        <span style={{ color: '#6b6560', fontWeight: 400 }}>
                          {' · '}{risk.high || 0} high · {risk.medium || 0} med · {risk.low || 0} low
                        </span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr style={{ background: '#1a1918', borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
                              {['Club', 'Risk Issues', 'Level', 'Recommended Action', 'Quick Action'].map(h => (
                                <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: '#6b6560' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {atRisk.map((item) => {
                              const col = RISK_COLORS[item.riskLevel] || RISK_COLORS.low;
                              const hasStaleDiscount = item.risks.some(r => r.type === 'stale_discount');
                              return (
                                <tr key={String(item.clubId)}
                                  style={{ background: '#1e1d1b', borderBottom: '1px solid rgba(237,232,224,0.08)', borderLeft: `3px solid ${col.text}40` }}
                                  className="hover:bg-[#28261f] transition-colors">
                                  <td className="px-4 py-3">
                                    <p className="font-semibold text-[#ede8e0]">{item.name}</p>
                                    <p className="text-xs mt-0.5" style={{ color: '#6b6560' }}>
                                      {item.plan} · {item.status}
                                      {item.memberCount != null && ` · ${item.memberCount} students`}
                                    </p>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex flex-wrap gap-1">
                                      {item.risks.map((r, ri) => {
                                        const rc = RISK_COLORS[r.severity] || RISK_COLORS.low;
                                        return (
                                          <span key={ri} className="text-xs font-semibold px-2 py-0.5 rounded"
                                            style={{ background: rc.bg, color: rc.text, border: `1px solid ${rc.border}` }}>
                                            {r.label}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className="text-xs font-bold uppercase" style={{ color: col.text }}>{col.label}</span>
                                  </td>
                                  <td className="px-4 py-3 max-w-xs">
                                    <p className="text-xs leading-relaxed" style={{ color: '#8a847c' }}>
                                      {item.risks[0]?.recommendedAction || '—'}
                                    </p>
                                    {item.risks.length > 1 && (
                                      <p className="text-xs mt-0.5" style={{ color: '#6b6560' }}>
                                        +{item.risks.length - 1} more issue{item.risks.length > 2 ? 's' : ''}
                                      </p>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex flex-col gap-1.5">
                                      <Link to={`/superadmin/clubs/${item.clubId}`}
                                        className="text-xs font-semibold hover:underline inline-flex items-center gap-1"
                                        style={{ color: '#c8102e' }}>
                                        View Club <MdOpenInNew style={{ fontSize: 11 }} />
                                      </Link>
                                      {hasStaleDiscount && (
                                        <Link to="/superadmin/discounts"
                                          className="text-xs font-semibold hover:underline inline-flex items-center gap-1"
                                          style={{ color: '#fbbf24' }}>
                                          Clean Discounts <MdOpenInNew style={{ fontSize: 11 }} />
                                        </Link>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {followUpsDue.length > 0 && (
                    <div style={{ borderTop: atRisk.length > 0 ? '1px solid rgba(237,232,224,0.08)' : 'none' }}>
                      <div className="px-4 py-2 flex items-center justify-between"
                        style={{ background: '#1a1918', borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
                        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#8a847c' }}>
                          Follow-ups due
                          <span style={{ color: '#6b6560', fontWeight: 400 }}>{' · '}{followUpsDue.length}</span>
                        </span>
                        <Link to="/superadmin/leads?status=needs_followup" className="text-xs hover:underline" style={{ color: '#6b6560' }}>View all →</Link>
                      </div>
                      {followUpsDue.map(lead => <FollowUpRow key={lead._id} lead={lead} />)}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>

          {/* 4 ── Sales (one compact row) ── */}
          <section>
            <SectionLabel action={<Link to="/superadmin/leads" className="text-xs hover:underline" style={{ color: '#6b6560' }}>All leads →</Link>}>
              Sales
            </SectionLabel>
            <div className="rounded-xl border p-4 space-y-3" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
              <div className="text-sm flex flex-wrap items-center gap-x-1.5">
                <span style={{ color: '#6b6560' }}>Pipeline:</span>
                <Link to="/superadmin/leads?status=new" className="hover:underline" style={{ color: '#60a5fa', fontWeight: 600 }}>{salesData?.newLeadsCount || 0} new</Link>
                <span style={{ color: '#6b6560' }}>·</span>
                <Link to="/superadmin/leads?status=needs_followup" className="hover:underline" style={{ color: '#fbbf24', fontWeight: 600 }}>{salesData?.followUpsDueCount || 0} due</Link>
                <span style={{ color: '#6b6560' }}>·</span>
                <Link to="/superadmin/leads?status=converted" className="hover:underline" style={{ color: '#34d399', fontWeight: 600 }}>{salesData?.convertedLeadsCount || 0} converted</Link>
                <span style={{ color: '#6b6560' }}>this month</span>
              </div>

              {demoUrl ? (
                <div className="flex flex-wrap items-center gap-2 pt-3" style={{ borderTop: '1px solid rgba(237,232,224,0.08)' }}>
                  <span className="text-xs flex-shrink-0" style={{ color: '#6b6560' }}>Sales demo:</span>
                  <code className="text-xs truncate px-2 py-1 rounded" style={{ background: '#1a1918', color: '#60a5fa', fontFamily: 'monospace', maxWidth: 260 }}>{demoUrl}</code>
                  <button onClick={copyDemo}
                    style={{ background: 'rgba(237,232,224,0.08)', color: '#8a847c', border: 'none', borderRadius: 6, padding: '4px 9px', fontSize: 11, cursor: 'pointer', flexShrink: 0 }}>
                    Copy
                  </button>
                  <a href={demoUrl} target="_blank" rel="noopener noreferrer"
                    style={{ background: 'rgba(200,16,46,0.15)', color: '#e01535', borderRadius: 6, padding: '4px 9px', fontSize: 11, textDecoration: 'none', flexShrink: 0 }}>
                    Open
                  </a>
                  <span className="text-xs" style={{ color: '#6b6560' }}>
                    <span style={{ color: '#a78bfa', fontWeight: 600 }}>{mainDemo.views7d || 0}</span> views/wk
                    {(mainDemo.totalViews || 0) > 0 && (
                      <> · <span style={{ color: '#34d399', fontWeight: 600 }}>{Math.round(((mainDemo.totalLeads || 0) / mainDemo.totalViews) * 100)}%</span> conversion</>
                    )}
                  </span>
                  <Link to="/superadmin/demo-links" className="text-xs hover:underline ml-auto flex-shrink-0" style={{ color: '#6b6560' }}>Manage →</Link>
                </div>
              ) : (
                <div className="flex items-center gap-2 pt-3 text-xs" style={{ borderTop: '1px solid rgba(237,232,224,0.08)', color: '#6b6560' }}>
                  <span>Demo not prepared yet</span>
                  <Link to="/superadmin/demo-links" className="hover:underline" style={{ color: '#c8102e' }}>Prepare main demo →</Link>
                </div>
              )}
            </div>
          </section>

          {/* 5 ── Recent activity ── */}
          <section>
            <SectionLabel>Recent Activity</SectionLabel>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

              {/* Platform actions */}
              <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
                <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }}>
                  <span className="text-xs font-semibold text-[#ede8e0]">Platform Actions</span>
                  <Link to="/superadmin/audit-logs" className="text-xs hover:underline" style={{ color: '#c8102e' }}>View all →</Link>
                </div>
                <div style={{ background: '#1e1d1b' }}>
                  {activity.length === 0 ? (
                    <div className="px-4 py-6 text-center text-xs" style={{ color: '#6b6560' }}>No recent activity</div>
                  ) : activity.map((log, i) => (
                    <div key={log._id || i}
                      className="flex items-center justify-between px-4 py-2.5 border-b last:border-b-0 hover:bg-[#28261f] transition-colors"
                      style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ACTION_ACCENT[log.action] || '#6b6560' }} />
                        <div className="min-w-0">
                          <span className="text-xs font-medium" style={{ color: ACTION_ACCENT[log.action] || '#c5bfb5' }}>
                            {humanizeAction(log.action)}
                          </span>
                          {log.targetName && <span className="text-xs" style={{ color: '#8a847c' }}> · {log.targetName}</span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <div className="text-xs" style={{ color: '#8a847c' }}>{log.actorName || '—'}</div>
                        <div className="text-xs" style={{ color: '#6b6560' }}>{fmtTimeAgo(log.timestamp)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent club access */}
              <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
                <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }}>
                  <span className="text-xs font-semibold text-[#ede8e0]">Recent Club Access</span>
                  <Link to="/superadmin/impersonation" className="text-xs hover:underline" style={{ color: '#c8102e' }}>View all →</Link>
                </div>
                <div style={{ background: '#1e1d1b' }}>
                  {impersonations.length === 0 ? (
                    <div className="px-4 py-6 text-center text-xs" style={{ color: '#6b6560' }}>No recent impersonation sessions</div>
                  ) : impersonations.map((s, i) => (
                    <div key={s._id || i}
                      className="flex items-start justify-between px-4 py-2.5 border-b last:border-b-0 hover:bg-[#28261f] transition-colors"
                      style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
                      <div className="flex items-start gap-2.5 min-w-0">
                        <FaUserSecret className="flex-shrink-0 mt-0.5" style={{ color: s.mode === 'full_access' ? '#f87171' : '#6b6560', fontSize: 12 }} />
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-[#ede8e0]">{s.targetClubName}</div>
                          <div className="text-xs" style={{ color: '#6b6560' }}>by {s.superAdminName} · {s.mode === 'full_access' ? 'Full access' : 'Read-only'}</div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <span className="text-xs px-1.5 py-0.5 rounded" style={{
                          background: s.status === 'active' ? 'rgba(16,185,129,0.15)' : '#28261f',
                          color:      s.status === 'active' ? '#34d399' : '#6b6560',
                        }}>
                          {s.status}
                        </span>
                        <div className="text-xs mt-0.5" style={{ color: '#6b6560' }}>{fmtTimeAgo(s.startedAt)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* 6 ── Footer: production health + quick actions ── */}
          <section className="space-y-3">
            <HealthPanel />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#6b6560' }}>Quick actions:</span>
              {quickActions.map(({ href, label, accent, count }, i) => (
                <span key={href} className="flex items-center gap-x-4">
                  {i > 0 && <span style={{ color: '#332f28' }}>·</span>}
                  <Link to={href} className="text-xs font-medium hover:underline" style={{ color: accent }}>
                    {label}
                    {count != null && count > 0 && <span style={{ color: '#6b6560' }}> ({count})</span>}
                  </Link>
                </span>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
